<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog
      as="div"
      class="relative"
      :initial-focus="elementWithInitialFocus"
      :modal="true"
      @close="emits('close')"
    >
      <TransitionChild
        v-if="!isFullScreen"
        enter="duration-200 ease-fast-effect transition-all"
        enter-from="opacity-0 "
        enter-to="opacity-100 "
        leave="duration-200 ease-spatial transition-all"
        leave-from="opacity-100 "
        leave-to="opacity-0 "
        as="template"
      >
        <BaseScrim :class="[isResponsive && 'hidden md:block', 'z-60']" />
      </TransitionChild>

      <div
        class="fixed inset-0 z-70"
        :class="[
          isBasic && 'flex items-center justify-center p-4',
          isResponsive && 'md:flex md:items-center md:justify-center md:p-4',
        ]"
      >
        <TransitionChild
          as="template"
          enter="duration-400 ease-fast-effect"
          enter-from="scale-50"
          enter-to="scale-100"
          leave="duration-100 ease-spatial"
          leave-from="scale-100"
          leave-to="scale-75"
        >
          <DialogPanel
            class="bg-surface-container-high scroll-track-surface-container-high rounded-extra-large origin-center transform overflow-hidden transition-all"
            as="div"
            :class="[
              attrs.class,
              isBasic && 'h-auto max-h-[80vh] max-w-140 min-w-70',
              isFullScreen && 'h-full w-full',
              isResponsive &&
                'md:rounded-extra-large h-full w-full rounded-none md:h-auto md:max-h-[80vh] md:max-w-140 md:min-w-70',
            ]"
          >
            <div
              class="relative flex h-full w-full flex-col"
              :class="[(isBasic || isResponsive) && 'md:max-h-[80vh]']"
            >
              <BaseFullScreenDialogTopBar
                v-if="isFullScreen || isResponsive"
                :class="[isResponsive && 'md:hidden']"
                :has-scrolled="hasScrolled"
                @close="$emit('close')"
              >
                <DialogTitle
                  v-if="title"
                  as="h2"
                  class="text-title-medium sm:text-headline-small text-on-surface mx-2"
                  >{{ title }}</DialogTitle
                ><template #actions><slot name="confirm" /></template>
              </BaseFullScreenDialogTopBar>
              <DialogTitle
                v-if="title && !isFullScreen"
                as="h2"
                class="border-outline-variant bg-surface-container-high text-on-surface text-headline-small sticky top-0 z-10 px-6 pt-6 pb-4"
                :class="[isResponsive && 'hidden md:block', hasScrolled && 'border-b']"
                >{{ title }}</DialogTitle
              >
              <DialogDescription
                v-if="description"
                as="p"
                class="text-body-medium text-on-surface-variant"
                :class="[isFullScreen && 'px-4', isBasic && 'px-6', isResponsive && 'px-4 md:px-6']"
                >{{ description }}</DialogDescription
              >
              <div
                v-if="hasContent"
                ref="container"
                class="grow overflow-y-auto"
                :class="[
                  isFullScreen && 'px-4',
                  isBasic && 'px-6',
                  isBasic && !hasButtons && 'pb-6',
                  isResponsive && 'p-4 md:px-6 md:py-0',
                ]"
              >
                <slot />
              </div>
              <div
                v-if="!isFullScreen && hasButtons"
                class="border-outline-variant bg-surface-container-high sticky bottom-0 flex-row justify-end gap-x-1 px-6 pt-4 pb-6"
                :class="[
                  isResponsive && 'hidden md:flex',
                  isBasic && 'flex',
                  !hasScrolledToTheEnd && 'border-t',
                ]"
              >
                <div
                  v-if="hasDissmis"
                  ref="dismiss"
                  :class="[alignDismissButtonRight && 'mr-auto']"
                >
                  <slot name="dismiss" />
                </div>
                <div v-if="hasConfirm" ref="confirm">
                  <slot name="confirm" />
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script setup lang="ts">
  import {
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogDescription,
    TransitionRoot,
    TransitionChild,
  } from '@headlessui/vue'
  import { computed, ref, useAttrs, useTemplateRef, watch } from 'vue'
  import BaseFullScreenDialogTopBar from './BaseFullScreenDialogTopBar.vue'
  import { useElementSize, useScroll } from '@vueuse/core'
  import BaseScrim from './BaseScrim.vue'
  import type { BaseDialogType } from './dialog-type'
  interface Props {
    title?: string
    description?: string
    type: BaseDialogType
    isOpen: boolean
    initialFocus?: (HTMLElement | null | undefined) | 'dismiss' | 'confirm'
    alignDismissButtonRight?: boolean
  }

  const props = defineProps<Props>()
  const emits = defineEmits(['close'])

  const isBasic = computed<boolean>(() => props.type == 'basic')
  const isFullScreen = computed<boolean>(() => props.type == 'full-screen')
  const isResponsive = computed<boolean>(() => props.type == 'responsive')

  const slots = defineSlots<{
    default?: () => __VLS_Slots
    dismiss?: () => __VLS_Slots
    confirm?: () => __VLS_Slots
  }>()

  const attrs = useAttrs()

  const hasContent = computed(() => {
    return !!slots.default
  })

  const hasConfirm = computed(() => {
    return !!slots.confirm
  })

  const hasDissmis = computed(() => {
    return !!slots.dismiss
  })

  const hasButtons = computed(() => {
    return hasDissmis.value || hasConfirm.value
  })

  const container = ref<HTMLDivElement | null>(null)
  const { y, arrivedState, measure } = useScroll(container)
  const { height } = useElementSize(container)
  watch(height, () => {
    measure()
  })
  const hasScrolled = computed<boolean>(() => y.value > 10)
  const hasScrolledToTheEnd = computed<boolean>(() => {
    if (container.value) {
      return arrivedState.bottom || container.value.scrollHeight <= container.value.clientHeight
    }
    return true
  })

  const dismiss = useTemplateRef<HTMLDivElement>('dismiss')
  const confirm = useTemplateRef<HTMLDivElement>('confirm')

  const elementWithInitialFocus = computed(() => {
    if (props.initialFocus === 'dismiss' && dismiss.value) {
      return dismiss.value.querySelector('button, a') as HTMLElement
    }

    if (props.initialFocus === 'confirm' && confirm.value) {
      return confirm.value.querySelector('button, a') as HTMLElement
    }

    if (props.initialFocus != 'confirm' && props.initialFocus != 'dismiss') {
      return props.initialFocus
    }

    return undefined
  })
</script>
